import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Users } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export default function ListsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [selected, setSelected] = useState(new Set());
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const [alsoDeleteContacts, setAlsoDeleteContacts] = useState(false);

  const load = () =>
    api.get("/lists").then(({ data }) => {
      setItems(data);
      setSelected(new Set());
    });
  useEffect(() => {
    load();
  }, []);

  const toggleSel = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };
  const toggleSelAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((l) => l.id)));
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", description: "" });
    setOpen(true);
  };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ name: l.name, description: l.description || "" });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    try {
      if (editing) {
        await api.patch(`/lists/${editing.id}`, form);
        toast.success("Lista aggiornata");
      } else {
        await api.post("/lists", form);
        toast.success("Lista creata");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questa lista?")) return;
    await api.delete(`/lists/${id}`);
    toast.success("Lista eliminata");
    load();
  };

  const runBulkDelete = async () => {
    if (!selected.size) return toast.error("Nessuna lista selezionata");
    try {
      const ids = Array.from(selected);
      // Optionally delete contacts belonging exclusively to these lists (contacts get list removed regardless)
      if (alsoDeleteContacts) {
        // Fetch contacts in each list and delete them
        for (const lid of ids) {
          const { data } = await api.get("/contacts", { params: { list_id: lid, limit: 1000 } });
          const contactIds = (data.items || []).map((c) => c.id);
          if (contactIds.length) {
            await api.post("/contacts/bulk-delete", { ids: contactIds });
          }
        }
      }
      const { data } = await api.post("/lists/bulk-delete", { ids });
      toast.success(
        data.deleted === 1
          ? "1 lista eliminata"
          : `${data.deleted} liste eliminate`
      );
      setOpenBulkDelete(false);
      setAlsoDeleteContacts(false);
      load();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };

  return (
    <>
      <PageHeader
        title="Liste"
        subtitle={
          selected.size > 0
            ? `${selected.size} liste selezionate`
            : "Organizza i tuoi contatti in gruppi per campagne mirate."
        }
        testid="lists-header"
        actions={
          <>
            {items.length > 0 && (
              <Button
                variant="outline"
                onClick={toggleSelAll}
                data-testid="lists-select-all"
              >
                {selected.size === items.length ? "Deseleziona" : "Seleziona tutte"}
              </Button>
            )}
            {selected.size > 0 && (
              <Button
                variant="destructive"
                onClick={() => setOpenBulkDelete(true)}
                data-testid="lists-bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Elimina {selected.size}
              </Button>
            )}
            <Button onClick={openNew} data-testid="new-list-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuova lista
            </Button>
          </>
        }
      />
      <PageBody>
        {items.length === 0 && (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <Users className="w-8 h-8 mx-auto text-muted-foreground mb-4" strokeWidth={1.25} />
            <h2 className="font-heading text-xl mb-2">Nessuna lista ancora</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Crea la tua prima lista per organizzare i contatti (clienti, prospect, ecc.).
            </p>
            <Button onClick={openNew} data-testid="empty-new-list">
              Crea la prima lista
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((l) => (
            <div
              key={l.id}
              className={`border bg-white p-5 flex flex-col transition-colors ${
                selected.has(l.id) ? "border-primary" : "border-border"
              }`}
              data-testid={`list-card-${l.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Checkbox
                    checked={selected.has(l.id)}
                    onCheckedChange={() => toggleSel(l.id)}
                    data-testid={`list-select-${l.id}`}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg truncate">{l.name}</div>
                    {l.description && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {l.description}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => del(l.id)}
                  data-testid={`delete-list-${l.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="font-heading text-2xl">{l.contact_count}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Contatti
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/contatti?list=${l.id}`)}
                    data-testid={`view-list-contacts-${l.id}`}
                  >
                    Vedi contatti
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(l)}
                    data-testid={`edit-list-${l.id}`}
                  >
                    Modifica
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PageBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="list-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica lista" : "Nuova lista"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="list-name-input"
              />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                data-testid="list-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button onClick={save} data-testid="list-save">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openBulkDelete} onOpenChange={setOpenBulkDelete}>
        <DialogContent data-testid="lists-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>
              Eliminare {selected.size} {selected.size === 1 ? "lista" : "liste"}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Le liste selezionate verranno eliminate definitivamente. I contatti al
              loro interno resteranno nella rubrica, ma perderanno l'associazione con queste liste.
            </p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={alsoDeleteContacts}
                onCheckedChange={(v) => setAlsoDeleteContacts(!!v)}
                data-testid="also-delete-contacts"
                className="mt-0.5"
              />
              <span>
                <strong>Elimina anche tutti i contatti</strong> presenti in queste liste
                <div className="text-xs text-muted-foreground mt-1">
                  Attenzione: se un contatto è anche in altre liste, verrà comunque eliminato.
                </div>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBulkDelete(false)} data-testid="lists-bulk-delete-cancel">
              Annulla
            </Button>
            <Button variant="destructive" onClick={runBulkDelete} data-testid="lists-bulk-delete-confirm">
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
